/**
 * Patch 35.2c · Hero image picker for a video production kit.
 *
 * Founder/Admin-only. Lists approved rendered image heads from the
 * same pack / brief / campaign, ranks the strongest starting frame,
 * runs the deterministic quality guard on selection, and enforces
 * an explicit risk-override before allowing risky renders.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ImagePlus, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/shared/ui/button";
import {
  assessHero,
  fetchHeroCandidates,
  fetchSignedMediaUrl,
  selectHero,
  type HeroCandidate,
  type HeroQualityResult,
} from "@/entities/ai-studio/api";

interface Props {
  workspaceId: string;
  assetId: string;
  currentHeroAssetId?: string | null;
  currentHeroVersion?: number | null;
  onChanged?: () => void;
}

export function HeroImagePicker({
  workspaceId, assetId, currentHeroAssetId, onChanged,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<HeroCandidate | null>(null);
  const [assessment, setAssessment] = useState<HeroQualityResult | null>(null);

  const list = useQuery({
    queryKey: ["hero-candidates", workspaceId, assetId],
    queryFn: () => fetchHeroCandidates(workspaceId, assetId),
    enabled: expanded,
  });

  const assessMut = useMutation({
    mutationFn: (hid: string) => assessHero(workspaceId, assetId, hid),
    onSuccess: (r) => setAssessment(r),
  });

  const chooseMut = useMutation({
    mutationFn: (hid: string) => selectHero(workspaceId, assetId, hid),
    onSuccess: () => {
      toast.success("Starting frame selected");
      // Patch 37.4.22f · cache-invalidation key alignment.
      // ContentOpsPage keys its pack-asset query as
      // ``["ops-pack-assets", activePackId]`` (see
      // ``ContentOpsPage.tsx:160``). The earlier ``["pack-assets"]``
      // key did not prefix-match, leaving ``body_json.hero_asset_id``
      // stale in the cache — the drawer kept rendering "Choose an
      // approved…" despite the canonical write landing correctly.
      // Invalidating both the Content Ops key and the Library
      // ``["lib"]`` prefix keeps the picker + the Vault drawer CTA
      // (Choose Starting Frame → Create Video) in lock-step with the
      // persisted state.
      void qc.invalidateQueries({ queryKey: ["ops-pack-assets"] });
      void qc.invalidateQueries({ queryKey: ["lib"] });
      onChanged?.();
      setExpanded(false);
    },
    onError: (e: unknown) => {
      const err = e as { detail?: { code?: string } };
      toast.error(err?.detail?.code || "Could not select this image");
    },
  });

  const heroSet = !!currentHeroAssetId;

  return (
    <div data-testid="hero-image-picker" className="space-y-2">
      <div className="flex items-center justify-between rounded-md border border-border/60 bg-secondary/20 p-3">
        <div className="flex items-center gap-3">
          <ImagePlus className="h-4 w-4 text-primary" />
          <div className="text-xs">
            <div className="font-medium">Starting frame</div>
            <div
              data-testid="hero-current-status"
              className="text-[11px] text-muted-foreground"
            >
              {heroSet
                ? "Approved image selected as the video's opening frame."
                : "Choose an approved campaign image as the video's opening frame."}
            </div>
          </div>
        </div>
        <Button
          data-testid="hero-picker-toggle"
          size="sm" variant={heroSet ? "outline" : "default"}
          onClick={() => setExpanded((v) => !v)}
        >
          {heroSet ? "Change image" : "Choose image"}
        </Button>
      </div>

      {expanded && (
        <div
          data-testid="hero-picker-panel"
          className="rounded-md border border-border/60 bg-background/60 p-3"
        >
          {list.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading candidates…
            </div>
          ) : (list.data?.candidates?.length ?? 0) === 0 ? (
            <div
              data-testid="hero-picker-empty"
              className="text-xs text-muted-foreground"
            >
              No approved rendered images available. Approve a rendered image
              via Content Review to make it selectable as a starting frame.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {list.data!.candidates.map((c, i) => (
                <HeroThumb
                  key={c.asset_id}
                  workspaceId={workspaceId}
                  candidate={c}
                  recommended={i === 0}
                  isSelected={selected?.asset_id === c.asset_id}
                  onSelect={() => {
                    setSelected(c);
                    setAssessment(null);
                    assessMut.mutate(c.asset_id);
                  }}
                />
              ))}
            </div>
          )}

          {selected && (
            <div
              data-testid="hero-assessment"
              className="mt-3 rounded-md border border-border/60 bg-secondary/30 p-3 text-xs"
            >
              {assessMut.isPending || !assessment ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Checking image quality…
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <QualityBadge level={assessment.quality_level} score={assessment.score} />
                    <span
                      data-testid={`hero-quality-summary-${assessment.quality_level}`}
                      className="text-muted-foreground"
                    >
                      {assessment.safe_summary}
                    </span>
                  </div>
                  {assessment.strengths.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                      {assessment.strengths.map((s) => (
                        <li key={s} className="flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3 text-emerald-500" /> {s}
                        </li>
                      ))}
                    </ul>
                  )}
                  {assessment.warnings.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-[11px] text-amber-600">
                      {assessment.warnings.map((w) => (
                        <li key={w} className="flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> {w}
                        </li>
                      ))}
                    </ul>
                  )}
                  {assessment.quality_level === "risky" && (
                    <div
                      data-testid="hero-risky-warning"
                      className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px]"
                    >
                      Failed provider attempts may still consume production credits.
                    </div>
                  )}
                  <div className="mt-3 flex justify-end">
                    <Button
                      data-testid="hero-select-confirm"
                      size="sm"
                      disabled={assessment.quality_level === "blocked" || chooseMut.isPending}
                      onClick={() => chooseMut.mutate(selected.asset_id)}
                    >
                      {chooseMut.isPending ? (
                        <>
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" /> Selecting…
                        </>
                      ) : (
                        "Use this image"
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HeroThumb({
  workspaceId, candidate, recommended, isSelected, onSelect,
}: {
  workspaceId: string;
  candidate: HeroCandidate;
  recommended: boolean;
  isSelected: boolean;
  onSelect: () => void;
}): JSX.Element {
  const signed = useQuery({
    queryKey: ["hero-thumb", workspaceId, candidate.thumbnail_asset_id],
    queryFn: () => fetchSignedMediaUrl(workspaceId, candidate.thumbnail_asset_id),
    staleTime: 4 * 60 * 1000,
  });
  return (
    <button
      data-testid={`hero-candidate-${candidate.asset_id}`}
      type="button"
      onClick={onSelect}
      className={`group relative overflow-hidden rounded-md border transition ${
        isSelected
          ? "border-primary ring-2 ring-primary"
          : "border-border/60 hover:border-primary/60"
      }`}
    >
      <div className="aspect-square w-full bg-secondary/40">
        {signed.data?.url ? (
          <img
            src={signed.data.url}
            alt="hero candidate"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
      {recommended && (
        <span
          data-testid="hero-recommended-badge"
          className="absolute left-1 top-1 rounded bg-primary px-1.5 py-0.5 text-[9px] font-medium text-primary-foreground"
        >
          Recommended
        </span>
      )}
      {candidate.same_pack && (
        <span className="absolute right-1 top-1 rounded bg-secondary/80 px-1.5 py-0.5 text-[9px] text-foreground">
          Same pack
        </span>
      )}
    </button>
  );
}

function QualityBadge({ level, score }: { level: string; score: number }): JSX.Element {
  const map: Record<string, string> = {
    strong: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
    acceptable: "bg-sky-500/15 text-sky-600 border-sky-500/30",
    risky: "bg-amber-500/15 text-amber-600 border-amber-500/30",
    blocked: "bg-rose-500/15 text-rose-600 border-rose-500/30",
  };
  return (
    <span
      data-testid={`hero-quality-badge-${level}`}
      className={`rounded-md border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${map[level] ?? ""}`}
    >
      {level} · {score}
    </span>
  );
}
